import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Loader2, AlertCircle, Clock, CheckCircle, LayoutDashboard, BookOpen, Zap } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Layout from "../../components/layout/Layout";
import TaskCard from "../../components/ui/TaskCard";
import TaskDrawer from "../../components/ui/TaskDrawer";

export default function KanbanBoard() {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const location = useLocation();

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const tasksRes = await fetch("/api/tasks", { headers: { "Authorization": `Bearer ${token}` } });
      
      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        // Here we keep DONE and CANCELLED out of main view, or put them in a final column
        setTasks(tasksData);
      }
    } catch (err) {
      console.error("Failed to load tasks", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteTask = async (taskId) => {
    try {
      const token = localStorage.getItem("obstuchim_token");
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      if (res.ok) {
        if (selectedTaskId === taskId) {
          setSelectedTaskId(null);
        }
        await fetchData();
      }
    } catch (err) {
      console.error("Failed to delete task", err);
    }
  };

  const handleUpdateTaskOptimistic = (taskId, updates) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...updates } : t));
  };

  // Group tasks
  const tasksTodo = tasks.filter(t => t.status === 'TODO');
  const tasksInProgress = tasks.filter(t => t.status === 'IN_PROGRESS');
  const tasksInReview = tasks.filter(t => t.status === 'IN_REVIEW' || t.status === 'REVIEW');
  const tasksDone = tasks.filter(t => t.status === 'DONE');

  return (
    <Layout>
      <div className="min-h-screen pt-20 pb-12 px-4 sm:px-6 lg:px-8 max-w-[1600px] mx-auto flex flex-col gap-8">
        
        {/* Navigation Tabs */}
        <div className="flex items-center gap-1 border-b border-neutral-800 pb-px">
          <Link 
            to="/workspace"
            className="px-4 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-200 transition-colors flex items-center gap-2 border-b-2 border-transparent"
          >
            <Zap className="w-4 h-4" />
            AI-Копайлот
          </Link>
          <Link 
            to="/workspace/kanban"
            className="px-4 py-2 text-sm font-medium text-primary-400 border-b-2 border-primary-500 flex items-center gap-2"
          >
            <LayoutDashboard className="w-4 h-4" />
            Доска задач
          </Link>
          <Link 
            to="/workspace/lore"
            className="px-4 py-2 text-sm font-medium text-neutral-400 hover:text-neutral-200 transition-colors border-b-2 border-transparent flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            База знаний
          </Link>
        </div>

        {/* Рабочая Панель (Канбан) */}
        <div className="flex-1 overflow-x-auto pb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 min-w-[1000px] relative z-10 h-full">
            
            {/* Колонка: В планах */}
            <div className="space-y-4 bg-neutral-900/20 p-4 rounded-xl border border-neutral-800/50">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-neutral-400 font-light flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-neutral-500" />
                  В планах
                </h3>
                <span className="text-xs bg-neutral-800 text-neutral-400 px-2 py-0.5 rounded-full">{tasksTodo.length}</span>
              </div>
              
              <AnimatePresence>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
                  </div>
                ) : tasksTodo.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onDelete={handleDeleteTask}
                    onClick={setSelectedTaskId}
                  />
                ))}
                {!isLoading && tasksTodo.length === 0 && (
                  <div className="text-center py-8 text-neutral-600 font-extralight text-sm border border-dashed border-neutral-800 rounded-lg">
                    Нет задач в планах
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Колонка: В Работе */}
            <div className="space-y-4 bg-neutral-900/20 p-4 rounded-xl border border-neutral-800/50">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-neutral-400 font-light flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  В работе
                </h3>
                <span className="text-xs bg-neutral-800 text-neutral-400 px-2 py-0.5 rounded-full">{tasksInProgress.length}</span>
              </div>
              
              <AnimatePresence>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
                  </div>
                ) : tasksInProgress.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onDelete={handleDeleteTask}
                    onClick={setSelectedTaskId}
                  />
                ))}
                {!isLoading && tasksInProgress.length === 0 && (
                  <div className="text-center py-8 text-neutral-600 font-extralight text-sm border border-dashed border-neutral-800 rounded-lg">
                    Пока нет активных задач
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Колонка: Ждут Приемки */}
            <div className="space-y-4 bg-neutral-900/20 p-4 rounded-xl border border-neutral-800/50">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-neutral-400 font-light flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-orange-400" />
                  На проверке
                </h3>
                <span className="text-xs bg-neutral-800 text-neutral-400 px-2 py-0.5 rounded-full">{tasksInReview.length}</span>
              </div>
              
              <AnimatePresence>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
                  </div>
                ) : tasksInReview.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onDelete={handleDeleteTask}
                    onClick={setSelectedTaskId}
                  />
                ))}
                {!isLoading && tasksInReview.length === 0 && (
                  <div className="text-center py-8 text-neutral-600 font-extralight text-sm border border-dashed border-neutral-800 rounded-lg">
                    Нет задач на проверке
                  </div>
                )}
              </AnimatePresence>
            </div>

            {/* Колонка: Завершено */}
            <div className="space-y-4 bg-neutral-900/20 p-4 rounded-xl border border-neutral-800/50 opacity-60">
              <div className="flex items-center justify-between border-b border-neutral-800 pb-2">
                <h3 className="text-neutral-500 font-light flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Завершено
                </h3>
                <span className="text-xs bg-neutral-800 text-neutral-500 px-2 py-0.5 rounded-full">{tasksDone.length}</span>
              </div>
              
              <AnimatePresence>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-neutral-600" />
                  </div>
                ) : tasksDone.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onDelete={handleDeleteTask}
                    onClick={setSelectedTaskId}
                  />
                ))}
              </AnimatePresence>
            </div>

          </div>
        </div>
      </div>
      
      {/* Drawer for Tasks */}
      <TaskDrawer
        task={tasks.find(t => t.id === selectedTaskId) || null}
        isOpen={!!selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        onUpdate={handleUpdateTaskOptimistic}
        onDelete={handleDeleteTask}
      />
    </Layout>
  );
}
